import { IonIcon } from '@ionic/react';
import { close, pause, play } from 'ionicons/icons';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import {
  setPersistentPlayerVisible,
  setPlaying,
} from '../../store/slices/audio-player-state.slice';
import { isVideoFile, toPlayableMediaUrl } from '../../utils/mediaFile';
import '../media-player/MediaPlayer.scss';

const formatTime = (seconds: number): string => {
  if (!Number.isFinite(seconds)) return '0:00';
  const minutes = Math.floor(seconds / 60);
  const remainder = Math.floor(seconds % 60);
  return `${minutes}:${remainder.toString().padStart(2, '0')}`;
};

/**
 * App-wide video preview bar. Audio rows render their own bar from
 * WaveformInline; this one owns the <video> element so playback keeps going
 * when the track row that started it is virtualized away.
 */
const StickyPlayer: React.FC = () => {
  const { isPlaying, persistentPlayerVisible, url } = useSelector(
    (s: RootState) => s.audioPlayerState,
  );
  const dispatch = useDispatch();
  const videoElement = useRef<HTMLVideoElement>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [previewUnavailable, setPreviewUnavailable] = useState(false);
  const visible = persistentPlayerVisible && isVideoFile(url);

  useEffect(() => {
    const video = videoElement.current;
    if (!visible || !video) return;

    if (isPlaying) {
      video.play().catch((error) => {
        // Switching videos or pausing interrupts a pending play() request.
        if (error?.name === 'AbortError') return;
        console.warn('Persistent video playback failed:', error);
        dispatch(setPlaying(false));
      });
    } else {
      video.pause();
    }
  }, [dispatch, isPlaying, url, visible]);

  const closePlayer = () => {
    dispatch(setPlaying(false));
    dispatch(setPersistentPlayerVisible(false));
  };

  const resetProgress = () => {
    setCurrentTime(0);
    setDuration(0);
    setPreviewUnavailable(false);
  };

  const seek = (event: React.ChangeEvent<HTMLInputElement>) => {
    const time = Number(event.target.value);
    if (videoElement.current) videoElement.current.currentTime = time;
    setCurrentTime(time);
  };

  if (!visible) return null;

  const fileName = url.split(/[\\/]/).pop() || 'Video';
  let title = fileName;
  try {
    title = decodeURIComponent(fileName);
  } catch {
    // Keep the original filename when it contains a literal percent sign.
  }
  title = title.replace(/\.[^/.]+$/, '');
  const seekable = Number.isFinite(duration) && duration > 0;

  return createPortal(
    <div className="persistent-player active video-player">
      <div
        className="player-button"
        onClick={() => dispatch(setPlaying(!isPlaying))}
      >
        <IonIcon icon={isPlaying ? pause : play} />
      </div>
      <div className="video-content">
        <video
          ref={videoElement}
          className="video-preview"
          src={toPlayableMediaUrl(url)}
          preload="metadata"
          onLoadStart={resetProgress}
          onDurationChange={(event) =>
            setDuration(event.currentTarget.duration)
          }
          onTimeUpdate={(event) =>
            setCurrentTime(event.currentTarget.currentTime)
          }
          onEnded={() => dispatch(setPlaying(false))}
          onError={() => {
            setPreviewUnavailable(true);
            dispatch(setPlaying(false));
          }}
        />
        <div className="video-info">
          <div className="video-title">{title}</div>
          {previewUnavailable ? (
            <div className="no-preview">Video preview unavailable</div>
          ) : (
            <div className="video-scrubber">
              <span className="video-time">{formatTime(currentTime)}</span>
              <input
                type="range"
                min={0}
                max={seekable ? duration : 0}
                step="any"
                value={currentTime}
                onChange={seek}
                disabled={!seekable}
                aria-label="Seek video preview"
              />
              <span className="video-time">{formatTime(duration)}</span>
            </div>
          )}
        </div>
      </div>
      <IonIcon
        icon={close}
        className="video-close"
        onClick={closePlayer}
        aria-label="Close video preview"
      />
    </div>,
    document.body,
  );
};

export default StickyPlayer;
